import { useCallback, useEffect, useRef } from 'react';
import { EventSourceParserStream } from 'eventsource-parser/stream';
import * as Sentry from '@sentry/react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// A turn acted on immediately after enqueueing can 404 until the background worker has created
// the turn row, so subscribe and cancel both retry for a short window before giving up.
const TURN_NOT_FOUND_RETRY_LIMIT = 10;
const TURN_NOT_FOUND_RETRY_DELAY_MS = 500;

type StreamChunkHandler = (rawData: string) => void;

// Whether the run ended because a newer stream superseded it (or it was cancelled), rather than
// finishing on its own. An aborted run must not run its trailing state cleanup: the chat hook is a
// single shared instance, so the run that aborted it now owns the shared state.
export type StreamOutcome = { aborted: boolean };

async function pipeEventStream(response: Response, onChunk: StreamChunkHandler): Promise<void> {
  if (response.body === null) return;

  const reader = response.body
    .pipeThrough(new TextDecoderStream())
    .pipeThrough(new EventSourceParserStream())
    .getReader();

  while (true) {
    const { value, done } = await reader.read();
    if (done || value.data === '\n\n') break;
    if (value.data) {
      onChunk(value.data);
    }
  }
}

export function useChatTransport() {
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const streamRequest = useCallback(
    async (
      url: string,
      body: object,
      onChunk: StreamChunkHandler,
      onError?: (error: unknown) => void,
    ): Promise<StreamOutcome> => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch stream: ${response.status} ${errorText}`);
        }

        await pipeEventStream(response, onChunk);
        return { aborted: false };
      } catch (error) {
        if (abortController.signal.aborted) return { aborted: true };
        Sentry.captureException(error);
        onError?.(error);
        return { aborted: false };
      }
    },
    [],
  );

  const subscribeToTurn = useCallback(
    async (
      url: string,
      onChunk: StreamChunkHandler,
      onError?: (error: unknown) => void,
    ): Promise<StreamOutcome> => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        let response: Response | null = null;
        for (let attempt = 0; attempt <= TURN_NOT_FOUND_RETRY_LIMIT; attempt += 1) {
          response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            signal: abortController.signal,
          });
          if (response.status !== 404 || attempt === TURN_NOT_FOUND_RETRY_LIMIT) break;
          await new Promise(resolve => setTimeout(resolve, TURN_NOT_FOUND_RETRY_DELAY_MS));
        }

        if (response === null || !response.ok) {
          throw new Error(`Failed to subscribe to turn stream: ${response?.status}`);
        }

        await pipeEventStream(response, onChunk);
        return { aborted: false };
      } catch (error) {
        if (abortController.signal.aborted) return { aborted: true };
        Sentry.captureException(error);
        onError?.(error);
        return { aborted: false };
      }
    },
    [],
  );

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const buildTurnCancelUrl = useCallback(
    (conversationId: string, turnId: string) =>
      `${API_URL}/conversations/${conversationId}/turns/${turnId}/cancel`,
    [],
  );

  // Tell the backend to stop the turn's background generation. Uses its own request rather than
  // the shared abort controller, so cancelling does not depend on the subscription still being
  // open, and retries the worker-start 404 window like the subscribe path.
  const cancelTurn = useCallback(
    async (conversationId: string, turnId: string): Promise<void> => {
      for (let attempt = 0; attempt <= TURN_NOT_FOUND_RETRY_LIMIT; attempt += 1) {
        try {
          const response = await fetch(buildTurnCancelUrl(conversationId, turnId), {
            method: 'POST',
            credentials: 'include',
          });
          if (response.status !== 404 || attempt === TURN_NOT_FOUND_RETRY_LIMIT) return;
        } catch (error) {
          Sentry.captureException(error);
          return;
        }
        await new Promise(resolve => setTimeout(resolve, TURN_NOT_FOUND_RETRY_DELAY_MS));
      }
    },
    [buildTurnCancelUrl],
  );

  const buildClientChatUrl = useCallback(
    (clientId: string) => `${API_URL}/clients/${clientId}/chat`,
    [],
  );

  const buildCrossClientChatUrl = useCallback(() => `${API_URL}/chat/general`, []);

  const buildFamilyChatUrl = useCallback(
    (familyId: string) => `${API_URL}/families/${familyId}/chat`,
    [],
  );

  const buildDocumentEditUrl = useCallback(
    (entityId: string) => `${API_URL}/documents/${entityId}/multi-turn-edit`,
    [],
  );

  const buildTurnStreamUrl = useCallback(
    (conversationId: string, turnId: string) =>
      `${API_URL}/conversations/${conversationId}/turns/${turnId}/stream`,
    [],
  );

  return {
    streamRequest,
    subscribeToTurn,
    cancelTurn,
    abort,
    buildClientChatUrl,
    buildCrossClientChatUrl,
    buildFamilyChatUrl,
    buildDocumentEditUrl,
    buildTurnStreamUrl,
  };
}
